// Reducer for the new Twitter-style Direct Messaging system.
// Follows the same Immutable.js pattern used by reducers/conversations.js.

import { Map as ImmutableMap, List as ImmutableList } from 'immutable';

import {
  DM_THREADS_FETCH_REQUEST,
  DM_THREADS_FETCH_SUCCESS,
  DM_THREADS_FETCH_FAIL,
  DM_MESSAGES_FETCH_REQUEST,
  DM_MESSAGES_FETCH_SUCCESS,
  DM_MESSAGES_FETCH_FAIL,
  DM_MESSAGE_SEND_SUCCESS,
  DM_THREAD_CREATE_SUCCESS,
  DM_MARK_READ_SUCCESS,
  DM_RECEIVE_MESSAGE,
  DM_DELETE_MESSAGE,
  DM_UPDATE_REACTION,
  DM_UPDATE_READ_RECEIPT,
  DM_UPDATE_THREAD,
  DM_DELETE_THREAD,
  DM_SET_ACTIVE_THREAD,
} from '../actions/dm_threads';

// ─── State shape ─────────────────────────────────────────────────────────────
//
//  dm_threads: ImmutableMap({
//    threads:          ImmutableMap<threadId, ImmutableMap<thread fields>>
//    threadOrder:      ImmutableList<threadId>   (sorted newest-first)
//    messages:         ImmutableMap<threadId, ImmutableMap<messageId, ImmutableMap<msg fields>>>
//    activeThreadId:   string | null
//    isLoadingThreads: boolean
//    hasMoreThreads:   boolean
//    isLoadingMessages: ImmutableMap<threadId, boolean>
//    hasMoreMessages:   ImmutableMap<threadId, boolean>
//  })

const initialState = ImmutableMap({
  threads:           ImmutableMap(),
  threadOrder:       ImmutableList(),
  messages:          ImmutableMap(),
  activeThreadId:    null,
  isLoadingThreads:  false,
  hasMoreThreads:    true,
  isLoadingMessages: ImmutableMap(),
  hasMoreMessages:   ImmutableMap(),
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const threadToMap = (t: Record<string, unknown>) =>
  ImmutableMap({
    id:             t['id'],
    is_group:       t['is_group'],
    title:          t['title'],
    last_message_at: t['last_message_at'],
    last_message_id: (t['last_message'] as Record<string, unknown> | null)?.['id'] ?? null,
    unread_count:   t['unread_count'] ?? 0,
    participant_ids: ImmutableList(
      ((t['participants'] ?? []) as Array<Record<string, unknown>>).map((a) => a['id']),
    ),
    participants: ImmutableList(
      ((t['participants'] ?? []) as Array<Record<string, unknown>>).map((a) =>
        ImmutableMap({ id: a['id'], username: a['username'], acct: a['acct'], avatar: a['avatar'] }),
      ),
    ),
  });

const messageToMap = (m: Record<string, unknown>) =>
  ImmutableMap({
    id:           m['id'],
    dm_thread_id: m['dm_thread_id'],
    account_id:   (m['account'] as Record<string, unknown>)?.['id'],
    text:         m['text'],
    deleted:      m['deleted'],
    reply_to_id:  m['reply_to_id'],
    created_at:   m['created_at'],
    read_by_ids:  ImmutableList((m['read_by_ids'] ?? []) as string[]),
    reactions:    ImmutableList(
      ((m['reactions'] ?? []) as Array<Record<string, unknown>>).map((r) =>
        ImmutableMap({
          emoji:       r['emoji'],
          count:       r['count'],
          account_ids: ImmutableList((r['account_ids'] ?? []) as string[]),
          me:          r['me'],
        }),
      ),
    ),
  });

const upsertThread = (
  state: ReturnType<typeof initialState>,
  thread: Record<string, unknown>,
) => {
  const id    = thread['id'] as string;
  const tMap  = threadToMap(thread);

  return state.withMutations((s) => {
    s.setIn(['threads', id], tMap);

    // Rebuild sorted order
    const order = s
      .get('threads')!
      .valueSeq()
      .sortBy((t) => t.get('last_message_at') ?? '')
      .reverse()
      .map((t) => t.get('id') as string)
      .toList();
    s.set('threadOrder', order);
  });
};

const upsertMessage = (
  state: ReturnType<typeof initialState>,
  message: Record<string, unknown>,
) => {
  const threadId  = message['dm_thread_id'] as string;
  const messageId = message['id'] as string;
  return state.setIn(['messages', threadId, messageId], messageToMap(message));
};

// ─── Reducer ─────────────────────────────────────────────────────────────────

type Action = { type: string; [key: string]: unknown };

export default function dm_threads(
  state: ReturnType<typeof initialState> = initialState,
  action: Action,
): ReturnType<typeof initialState> {
  switch (action.type) {

  // Thread list loading
  case DM_THREADS_FETCH_REQUEST:
    return state.set('isLoadingThreads', true);

  case DM_THREADS_FETCH_FAIL:
    return state.set('isLoadingThreads', false);

  case DM_THREADS_FETCH_SUCCESS: {
    const threads = action.threads as Array<Record<string, unknown>>;
    return state.withMutations((s) => {
      threads.forEach((t) => {
        s.setIn(['threads', t['id'] as string], threadToMap(t));
      });
      const order = s
        .get('threads')!
        .valueSeq()
        .sortBy((t) => t.get('last_message_at') ?? '')
        .reverse()
        .map((t) => t.get('id') as string)
        .toList();
      s.set('threadOrder', order);
      s.set('isLoadingThreads', false);
      if (!action.next) s.set('hasMoreThreads', false);
    });
  }

  // New thread created
  case DM_THREAD_CREATE_SUCCESS:
    return upsertThread(state, action.thread as Record<string, unknown>);

  // Message list loading
  case DM_MESSAGES_FETCH_REQUEST:
    return state.setIn(['isLoadingMessages', action.threadId as string], true);

  case DM_MESSAGES_FETCH_FAIL:
    return state.setIn(['isLoadingMessages', action.threadId as string], false);

  case DM_MESSAGES_FETCH_SUCCESS: {
    const threadId = action.threadId as string;
    const messages = action.messages as Array<Record<string, unknown>>;
    return state.withMutations((s) => {
      messages.forEach((m) => {
        s.setIn(['messages', threadId, m['id'] as string], messageToMap(m));
      });
      s.setIn(['isLoadingMessages', threadId], false);
      if (!action.next) s.setIn(['hasMoreMessages', threadId], false);
    });
  }

  // Message sent (optimistically add to state)
  case DM_MESSAGE_SEND_SUCCESS:
    return upsertMessage(state, action.message as Record<string, unknown>);

  // Mark thread as read — zero the unread_count
  case DM_MARK_READ_SUCCESS:
    return state.setIn(['threads', action.threadId as string, 'unread_count'], 0);

  // Active thread selection
  case DM_SET_ACTIVE_THREAD:
    return state.set('activeThreadId', action.threadId);

  // WebSocket: new message received
  case DM_RECEIVE_MESSAGE: {
    const msg      = action.payload as Record<string, unknown>;
    const threadId = msg['dm_thread_id'] as string;
    return upsertMessage(state, msg)
      .updateIn(['threads', threadId, 'unread_count'], (c: unknown) =>
        ((c as number) ?? 0) + 1,
      )
      .setIn(['threads', threadId, 'last_message_id'], msg['id'])
      .setIn(['threads', threadId, 'last_message_at'], msg['created_at']);
  }

  // WebSocket: message soft-deleted
  case DM_DELETE_MESSAGE: {
    const payload  = action.payload as Record<string, unknown>;
    const threadId = payload['dm_thread_id'] as string;
    const msgId    = payload['id'] as string;
    return state.setIn(['messages', threadId, msgId, 'deleted'], true);
  }

  // WebSocket: emoji reaction added or removed
  case DM_UPDATE_REACTION: {
    const payload  = action.payload as Record<string, unknown>;
    const threadId = (state.getIn(['messages']) as ImmutableMap<string, unknown>)
      .findKey((_msgs, tid) => state.hasIn(['messages', tid, payload['dm_message_id'] as string])) as string | undefined
      ?? payload['dm_thread_id'] as string;
    const msgId    = payload['dm_message_id'] as string;

    // Update the reactions list for this message
    return state.updateIn(
      ['messages', threadId, msgId, 'reactions'],
      (reactions: unknown) => {
        const list = (reactions as ImmutableList<ImmutableMap<string, unknown>>) ?? ImmutableList();
        const emoji = payload['emoji'] as string;
        const idx   = list.findIndex((r) => r.get('emoji') === emoji);

        if (payload['action'] === 'remove' && payload['count'] === 0) {
          return idx >= 0 ? list.delete(idx) : list;
        }

        const newReaction = ImmutableMap({
          emoji:       emoji,
          count:       payload['count'],
          account_ids: ImmutableList((payload['account_ids'] ?? []) as string[]),
          me:          payload['me'],
        });

        return idx >= 0 ? list.set(idx, newReaction) : list.push(newReaction);
      },
    );
  }

  // WebSocket: read receipt from another participant
  case DM_UPDATE_READ_RECEIPT: {
    const payload    = action.payload as Record<string, unknown>;
    const threadId   = payload['dm_thread_id'] as string;
    const msgId      = payload['dm_message_id'] as string;
    const accountId  = payload['account_id'] as string;
    return state.updateIn(
      ['messages', threadId, msgId, 'read_by_ids'],
      (ids: unknown) => {
        const list = (ids as ImmutableList<string>) ?? ImmutableList();
        return list.includes(accountId) ? list : list.push(accountId);
      },
    );
  }

  // WebSocket: thread metadata changed (rename, participant join/leave)
  case DM_UPDATE_THREAD:
    return upsertThread(state, action.payload as Record<string, unknown>);

  // WebSocket: thread deleted
  case DM_DELETE_THREAD: {
    const payload  = action.payload as Record<string, unknown>;
    const threadId = payload['id'] as string;
    return state.withMutations((s) => {
      s.deleteIn(['threads', threadId]);
      s.deleteIn(['messages', threadId]);
      s.update('threadOrder', (order) => (order as ImmutableList<string>).filter((id) => id !== threadId));
      if (s.get('activeThreadId') === threadId) s.set('activeThreadId', null);
    });
  }

  default:
    return state;
  }
}
